import { supabase } from '../lib/supabase';

export const treatmentAnalyticsService = {
  async getAllAnalytics(filters = {}) {
    let query = supabase?.from('treatment_analytics')?.select(`
        *,
        created_by_profile:user_profiles!treatment_analytics_created_by_fkey(full_name, email),
        task:ipm_tasks(id, title, description, task_type),
        product:products(id, name, active_ingredients, category)
      `)?.order('treatment_date', { ascending: false });

    // Apply filters
    if (filters?.dateRange) {
      const { startDate, endDate } = this.parseDateRange(filters?.dateRange);
      if (startDate && endDate) {
        query = query?.gte('treatment_date', startDate)?.lte('treatment_date', endDate);
      }
    }

    if (filters?.cropType && filters?.cropType !== 'all') {
      query = query?.eq('crop_type', filters?.cropType);
    }

    if (filters?.fieldLocation && filters?.fieldLocation !== 'all') {
      query = query?.eq('field_location', filters?.fieldLocation);
    }

    if (filters?.effectiveness) {
      switch (filters?.effectiveness) {
        case 'excellent':
          query = query?.gte('effectiveness_rating', 90);
          break;
        case 'good':
          query = query?.gte('effectiveness_rating', 70)?.lt('effectiveness_rating', 90);
          break;
        case 'fair':
          query = query?.gte('effectiveness_rating', 50)?.lt('effectiveness_rating', 70);
          break;
        case 'poor':
          query = query?.lt('effectiveness_rating', 50);
          break;
      }
    }

    const { data, error } = await query;
    
    if (error) throw error;
    return data?.map(record => ({
      id: record?.id,
      companyId: record?.company_id,
      createdBy: record?.created_by,
      createdByProfile: record?.created_by_profile,
      taskId: record?.task_id,
      task: record?.task,
      productId: record?.product_id,
      product: record?.product,
      fieldLocation: record?.field_location,
      cropType: record?.crop_type,
      treatmentDate: record?.treatment_date,
      applicationRate: record?.application_rate,
      weatherConditions: record?.weather_conditions || {},
      effectivenessRating: record?.effectiveness_rating,
      costPerAcre: record?.cost_per_acre,
      notes: record?.notes,
      createdAt: record?.created_at
    }));
  },

  async getAnalyticsSummary(filters = {}) {
    const analytics = await this.getAllAnalytics(filters);
    
    const summary = {
      totalTreatments: analytics?.length,
      averageEffectiveness: 0,
      totalCost: 0,
      averageCostPerAcre: 0,
      cropTypeBreakdown: {},
      effectivenessDistribution: {
        excellent: 0,
        good: 0,
        fair: 0,
        poor: 0
      },
      monthlyTrends: {}
    };

    if (analytics?.length === 0) return summary;

    let totalEffectiveness = 0;
    let totalCost = 0;
    let recordsWithCost = 0;

    analytics?.forEach(record => {
      // Effectiveness
      if (record?.effectivenessRating) {
        totalEffectiveness += record?.effectivenessRating;
        
        // Effectiveness distribution
        if (record?.effectivenessRating >= 90) {
          summary.effectivenessDistribution.excellent++;
        } else if (record?.effectivenessRating >= 70) {
          summary.effectivenessDistribution.good++;
        } else if (record?.effectivenessRating >= 50) {
          summary.effectivenessDistribution.fair++;
        } else {
          summary.effectivenessDistribution.poor++;
        }
      }

      // Cost
      if (record?.costPerAcre) {
        totalCost += record?.costPerAcre;
        recordsWithCost++;
      }

      // Crop type breakdown
      const cropType = record?.cropType || 'Unknown';
      summary.cropTypeBreakdown[cropType] = (summary?.cropTypeBreakdown?.[cropType] || 0) + 1;

      // Monthly trends
      const month = new Date(record.treatmentDate)?.toISOString()?.substring(0, 7); // YYYY-MM
      if (!summary?.monthlyTrends?.[month]) {
        summary.monthlyTrends[month] = { treatments: 0, totalEffectiveness: 0, count: 0 };
      }
      summary.monthlyTrends[month].treatments++;
      if (record?.effectivenessRating) {
        summary.monthlyTrends[month].totalEffectiveness += record?.effectivenessRating;
        summary.monthlyTrends[month].count++;
      }
    });

    summary.averageEffectiveness = totalEffectiveness / analytics?.length;
    summary.totalCost = totalCost;
    summary.averageCostPerAcre = recordsWithCost > 0 ? totalCost / recordsWithCost : 0;

    // Calculate average effectiveness for monthly trends
    Object.keys(summary?.monthlyTrends)?.forEach(month => {
      let trend = summary?.monthlyTrends?.[month];
      trend.averageEffectiveness = trend?.count > 0 ? trend?.totalEffectiveness / trend?.count : 0;
    });

    return summary;
  },

  async createAnalytics(analyticsData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get user's company ID
    const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', user?.id)?.single();

    if (!employeeData) throw new Error('User not associated with a company');

    const { data, error } = await supabase?.from('treatment_analytics')?.insert({
        company_id: employeeData?.company_id,
        created_by: user?.id,
        task_id: analyticsData?.taskId,
        product_id: analyticsData?.productId,
        field_location: analyticsData?.fieldLocation,
        crop_type: analyticsData?.cropType,
        treatment_date: analyticsData?.treatmentDate,
        application_rate: analyticsData?.applicationRate,
        weather_conditions: analyticsData?.weatherConditions || {},
        effectiveness_rating: analyticsData?.effectivenessRating,
        cost_per_acre: analyticsData?.costPerAcre,
        notes: analyticsData?.notes
      })?.select()?.single();
    
    if (error) throw error;
    return {
      id: data?.id,
      companyId: data?.company_id,
      createdBy: data?.created_by,
      taskId: data?.task_id,
      productId: data?.product_id,
      fieldLocation: data?.field_location,
      cropType: data?.crop_type,
      treatmentDate: data?.treatment_date,
      applicationRate: data?.application_rate,
      weatherConditions: data?.weather_conditions,
      effectivenessRating: data?.effectiveness_rating,
      costPerAcre: data?.cost_per_acre,
      notes: data?.notes,
      createdAt: data?.created_at
    };
  },

  async getOverviewMetrics(options = {}) {
    try {
      const { userId } = options;

      // If no userId provided, return demo data for non-authenticated users
      if (!userId) {
        return {
          total_treatments: 45,
          avg_effectiveness: 87.3,
          active_ipm_programs: 12,
          cost_savings: 2847.50,
          recent_treatments: 8,
          success_rate: 92.1
        };
      }

      // Get user's company ID for authenticated users
      const { data: employeeData } = await supabase?.from('company_employees')?.select('company_id')?.eq('user_id', userId)?.single();

      if (!employeeData) {
        // User not associated with company, return demo data
        return {
          total_treatments: 0,
          avg_effectiveness: 0,
          active_ipm_programs: 0,
          cost_savings: 0,
          recent_treatments: 0,
          success_rate: 0
        };
      }

      // Get treatment analytics for the user's company
      const { data: treatments, error } = await supabase?.from('treatment_analytics')?.select('*')?.eq('company_id', employeeData?.company_id);

      if (error) {
        console.error('Error fetching treatment analytics:', error);
        throw error;
      }

      // Calculate metrics
      const totalTreatments = treatments?.length || 0;
      const avgEffectiveness = treatments?.length > 0 
        ? treatments?.reduce((sum, t) => sum + (t?.effectiveness_rating || 0), 0) / treatments?.length
        : 0;

      // Count treatments in last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
      const recentTreatments = treatments?.filter(t => 
        new Date(t?.treatment_date) >= thirtyDaysAgo
      )?.length || 0;

      // Calculate success rate (treatments with >70% effectiveness)
      const successfulTreatments = treatments?.filter(t => 
        (t?.effectiveness_rating || 0) > 70
      )?.length || 0;
      const successRate = totalTreatments > 0 ? (successfulTreatments / totalTreatments) * 100 : 0;

      // Calculate cost savings (mock calculation based on effectiveness)
      const costSavings = treatments?.reduce((sum, t) => {
        const effectiveness = t?.effectiveness_rating || 0;
        const cost = t?.cost_per_acre || 0;
        return sum + (cost * (effectiveness / 100) * 0.3); // 30% savings factor
      }, 0) || 0;

      // Count active IPM programs (unique crop types with treatments in last 90 days)
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo?.setDate(ninetyDaysAgo?.getDate() - 90);
      const activeCropTypes = new Set(
        treatments?.filter(t => new Date(t?.treatment_date) >= ninetyDaysAgo)?.map(t => t?.crop_type)
      );

      return {
        total_treatments: totalTreatments,
        avg_effectiveness: Math.round(avgEffectiveness * 10) / 10,
        active_ipm_programs: activeCropTypes?.size || 0,
        cost_savings: Math.round(costSavings * 100) / 100,
        recent_treatments: recentTreatments,
        success_rate: Math.round(successRate * 10) / 10
      };
    } catch (error) {
      console.error('Error in getOverviewMetrics:', error);
      // Return fallback data on error
      return {
        total_treatments: 0,
        avg_effectiveness: 0,
        active_ipm_programs: 0,
        cost_savings: 0,
        recent_treatments: 0,
        success_rate: 0
      };
    }
  },

  async exportData(filters = {}, format = 'csv') {
    const analytics = await this.getAllAnalytics(filters);
    
    if (format === 'csv') {
      return this.generateCSV(analytics);
    } else if (format === 'json') {
      return JSON.stringify(analytics, null, 2);
    }
    
    throw new Error('Unsupported export format');
  },

  generateCSV(data) {
    if (!data || data?.length === 0) return '';
    
    const headers = [
      'Date',
      'Field Location',
      'Crop Type',
      'Product Name',
      'Application Rate',
      'Effectiveness %',
      'Cost per Acre',
      'Weather Conditions',
      'Notes'
    ];
    
    const rows = data?.map(record => [
      new Date(record.treatmentDate)?.toISOString()?.split('T')?.[0],
      record?.fieldLocation || '',
      record?.cropType || '',
      record?.product?.name || '',
      record?.applicationRate || '',
      record?.effectivenessRating || '',
      record?.costPerAcre || '',
      JSON.stringify(record?.weatherConditions || {}),
      record?.notes || ''
    ]);
    
    const csvContent = [headers, ...rows]?.map(row => row?.map(field => `"${field}"`)?.join(','))?.join('\n');
    
    return csvContent;
  },

  parseDateRange(dateRange) {
    const now = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case 'last-7-days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case 'last-30-days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case 'last-90-days':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case 'current-season':
        // Assume growing season is March to October
        const currentYear = now?.getFullYear();
        startDate = new Date(currentYear, 2, 1); // March 1st
        endDate = new Date(currentYear, 9, 31); // October 31st
        break;
      case 'last-season':
        const lastYear = now?.getFullYear() - 1;
        startDate = new Date(lastYear, 2, 1); // March 1st last year
        endDate = new Date(lastYear, 9, 31); // October 31st last year
        break;
      default:
        return { startDate: null, endDate: null };
    }

    return {
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString()
    };
  },

  async getTreatmentRankings(filters = {}) {
    try {
      // Get all analytics data with filters
      const analytics = await this.getAllAnalytics(filters);
      
      if (!analytics || analytics?.length === 0) {
        return [];
      }

      // Group analytics by product to calculate rankings
      const productRankings = {};
      
      analytics?.forEach(record => {
        const productId = record?.productId;
        const productName = record?.product?.name || 'Unknown Product';
        
        if (!productRankings?.[productId]) {
          productRankings[productId] = {
            product_id: productId,
            product_name: productName,
            effectiveness_scores: [],
            usage_count: 0,
            total_cost: 0,
            total_acres: 0,
            ratings: []
          };
        }
        
        // Accumulate data
        if (record?.effectivenessRating) {
          productRankings?.[productId]?.effectiveness_scores?.push(record?.effectivenessRating);
        }
        
        productRankings[productId].usage_count++;
        
        if (record?.costPerAcre) {
          productRankings[productId].total_cost += record?.costPerAcre;
          productRankings[productId].total_acres++;
        }
      });

      // Calculate rankings metrics
      const rankings = Object.values(productRankings)?.map(data => {
        const effectiveness_score = data?.effectiveness_scores?.length > 0
          ? data?.effectiveness_scores?.reduce((sum, score) => sum + score, 0) / data?.effectiveness_scores?.length
          : null;
        
        const avg_cost_per_acre = data?.total_acres > 0
          ? data?.total_cost / data?.total_acres
          : null;
        
        // Calculate ROI based on effectiveness and cost
        let roi_percentage = null;
        if (effectiveness_score && avg_cost_per_acre) {
          // Simple ROI calculation: higher effectiveness and lower cost = better ROI
          const baseline_cost = 50; // Baseline cost per acre for comparison
          const cost_efficiency = baseline_cost / avg_cost_per_acre;
          roi_percentage = (effectiveness_score / 100) * cost_efficiency * 100;
        }
        
        // Determine trend based on recent performance
        let trend = 'stable';
        if (effectiveness_score >= 85) {
          trend = 'up';
        } else if (effectiveness_score < 70) {
          trend = 'down';
        }
        
        return {
          id: `ranking_${data?.product_id}`,
          product_id: data?.product_id,
          product_name: data?.product_name,
          effectiveness_score: effectiveness_score ? Math.round(effectiveness_score * 10) / 10 : null,
          usage_count: data?.usage_count,
          avg_cost_per_acre: avg_cost_per_acre ? Math.round(avg_cost_per_acre * 100) / 100 : null,
          roi_percentage: roi_percentage ? Math.round(roi_percentage * 10) / 10 : null,
          user_rating: 0, // Placeholder for future rating system
          trend
        };
      });

      // Sort rankings based on filters
      const { sortBy = 'effectiveness', sortOrder = 'desc' } = filters;
      
      rankings?.sort((a, b) => {
        let aValue = a?.[sortBy] ?? -Infinity;
        let bValue = b?.[sortBy] ?? -Infinity;
        
        if (sortOrder === 'desc') {
          return bValue - aValue;
        } else {
          return aValue - bValue;
        }
      });

      return rankings;
    } catch (error) {
      console.error('Error getting treatment rankings:', error);
      throw new Error(`Failed to get treatment rankings: ${error?.message}`);
    }
  }
};

export default treatmentAnalyticsService;